import React from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  Toolbar,
  List,
  Typography,
  Divider,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  CloudQueue as CloudIcon,
  Public as RegionIcon,
  Computer as VMIcon,
  DesktopWindows as OSIcon,
  Storage as StorageIcon,
  TableChart as DatabaseIcon,
  SettingsApplications as SettingsAppIcon,
  Category as CategoryIcon,
  Tune as TuneIcon,
  Extension as AddonIcon,
  AttachMoney as MoneyIcon,
  Layers as EnvironmentIcon,
  People as PeopleIcon,
  History as HistoryIcon,
  Sync as SyncIcon,
  CloudQueue as CloudSyncIcon,
  Backup as BackupIcon,
  Storage as DbStorageIcon,
  Science as ScienceIcon,
} from '@mui/icons-material';
import AppHeader from '../../components/AppHeader';

const drawerWidth = 280;

interface MenuItem {
  name: string;
  path: string;
  icon: React.ReactNode;
}

interface MenuSection {
  section: string;
  items: MenuItem[];
}

const AdminLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const menuItems: MenuSection[] = [
    {
      section: 'Configuration',
      items: [
        { name: 'Providers', path: '/admin/providers', icon: <CloudIcon /> },
        { name: 'Regions', path: '/admin/regions', icon: <RegionIcon /> },
        { name: 'VM Types', path: '/admin/vm-types', icon: <VMIcon /> },
        { name: 'OS Types', path: '/admin/os-types', icon: <OSIcon /> },
        { name: 'Disks', path: '/admin/disks', icon: <StorageIcon /> },
        { name: 'Database Types', path: '/admin/database-types', icon: <DatabaseIcon /> },
        { name: 'Database Config (PaaS)', path: '/admin/database-configs', icon: <SettingsAppIcon /> },
        { name: 'Database Config (VM)', path: '/admin/database-editions', icon: <CategoryIcon /> },
        { name: 'Addons', path: '/admin/addons', icon: <AddonIcon /> },
        { name: 'Addon Options', path: '/admin/addon-options', icon: <TuneIcon /> },
        { name: 'Power Types', path: '/admin/power-types', icon: <VMIcon /> },
        { name: 'Power Addons', path: '/admin/power-addons', icon: <AddonIcon /> },
        { name: 'Power Addon Options', path: '/admin/power-addon-options', icon: <TuneIcon /> },
      ],
    },
    {
      section: 'Pricing',
      items: [
        { name: 'VM Pricing', path: '/admin/pricing/vms', icon: <MoneyIcon /> },
        { name: 'Disk Pricing', path: '/admin/pricing/disks', icon: <MoneyIcon /> },
        { name: 'Database Pricing (PaaS)', path: '/admin/pricing/databases', icon: <MoneyIcon /> },
        { name: 'SQL Server on VM Pricing', path: '/admin/pricing/database-vms', icon: <MoneyIcon /> },
        { name: 'Addon Pricing', path: '/admin/pricing/addons', icon: <MoneyIcon /> },
        { name: 'Addon Option Pricing', path: '/admin/pricing/addon-options', icon: <MoneyIcon /> },
        { name: 'Price Import', path: '/admin/pricing/import', icon: <CloudSyncIcon /> },
        { name: 'Power VM Pricing', path: '/admin/pricing/power-vms', icon: <MoneyIcon /> },
        { name: 'Power Disk Pricing', path: '/admin/pricing/power-disks', icon: <MoneyIcon /> },
        { name: 'Power Addon Pricing', path: '/admin/pricing/power-addons', icon: <MoneyIcon /> },
        { name: 'Power Addon Option Pricing', path: '/admin/pricing/power-addon-options', icon: <MoneyIcon /> },
        { name: 'VM Backup Pricing', path: '/admin/pricing/backup-vms', icon: <BackupIcon /> },
        { name: 'DB Backup Pricing', path: '/admin/pricing/backup-databases', icon: <BackupIcon /> },
        { name: 'DB Extra Storage Pricing', path: '/admin/pricing/extra-storage', icon: <DbStorageIcon /> },
        { name: 'Enhanced DR Pricing', path: '/admin/pricing/dr-testing', icon: <ScienceIcon /> },
      ],
    },
    {
      section: 'Settings',
      items: [
        { name: 'API Mappings', path: '/admin/api-mappings', icon: <SyncIcon /> },
        { name: 'Environments', path: '/admin/environments', icon: <EnvironmentIcon /> },
        { name: 'Default Addons', path: '/admin/default-addons', icon: <TuneIcon /> },
        { name: 'Power Default Addons', path: '/admin/power-default-addons', icon: <TuneIcon /> },
        { name: 'Users', path: '/admin/users', icon: <PeopleIcon /> },
        { name: 'Audit Log', path: '/admin/audit-log', icon: <HistoryIcon /> },
      ],
    },
  ];

  return (
    <Box sx={{ display: 'flex' }}>
      <AppHeader
        position="fixed"
        sx={{ zIndex: (theme: { zIndex: { drawer: number } }) => theme.zIndex.drawer + 1 }}
      />

      <Drawer
        variant="permanent"
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
          },
        }}
      >
        <Toolbar />
        <Box sx={{ overflow: 'auto' }}>
          <Box sx={{ px: 2, py: 1.5, textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              v2.2.0 — 7th July 2026
            </Typography>
          </Box>
          <Divider />
          {menuItems.map((section, sectionIndex) => (
            <Box key={section.section}>
              <Box sx={{ px: 2, py: 1.5, bgcolor: 'grey.100' }}>
                <Typography variant="overline" color="text.secondary" fontWeight="bold">
                  {section.section}
                </Typography>
              </Box>
              <List>
                {section.items.map((item) => (
                  <ListItem key={item.path} disablePadding>
                    <ListItemButton
                      selected={location.pathname === item.path}
                      onClick={() => navigate(item.path)}
                    >
                      <ListItemIcon>{item.icon}</ListItemIcon>
                      <ListItemText primary={item.name} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
              {sectionIndex < menuItems.length - 1 && <Divider />}
            </Box>
          ))}
        </Box>
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          bgcolor: 'background.default',
          p: 2,
        }}
      >
        <Toolbar />
        <Outlet />
      </Box>
    </Box>
  );
};

export default AdminLayout;
